import { type AnyRoute, createRoute } from "@tanstack/react-router";
import { lazy, type ReactNode } from "react";
import { parseSection, type SettingsSection } from "../types";

// Lazily loaded — settings is a secondary surface, not part of the /chat landing.
const SettingsPage = lazy(() =>
  import("../components/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);

export interface SettingsRouteOptions {
  /** Account tab content — owned by the auth feature, injected by `app`. */
  accountSection: ReactNode;
  /** Billing tab content — owned by the billing feature, injected by `app`. */
  billingSection: ReactNode;
}

/** Settings route — composed into the app shell layout in `app/router.tsx`. */
export function createSettingsRoute<TParent extends AnyRoute>(
  parent: TParent,
  { accountSection, billingSection }: SettingsRouteOptions,
) {
  return createRoute({
    getParentRoute: () => parent,
    path: "/settings",
    // `connected` / `connector_error` are the MCP OAuth callback's return
    // params — optional pass-throughs the Connectors section consumes once.
    validateSearch: (
      search: Record<string, unknown>,
    ): { section: SettingsSection; connected?: string; connector_error?: string } => ({
      section: parseSection(search.section),
      ...(typeof search.connected === "string" && search.connected
        ? { connected: search.connected }
        : {}),
      ...(typeof search.connector_error === "string" && search.connector_error
        ? { connector_error: search.connector_error }
        : {}),
    }),
    component: () => (
      <SettingsPage
        accountSection={accountSection}
        billingSection={billingSection}
      />
    ),
  });
}
